/**
 * Single source of truth for how a workflow/run/step status is presented across
 * the whole workflows feature — the label (i18n key), the leading icon, the pill
 * tone, the standalone-icon color, and whether it spins.
 *
 * Before this module the same status→{label,icon,color} mapping was hand-rolled in
 * four places (the runs list, the run detail, the builder shell, and WorkflowCard),
 * and they disagreed on which statuses existed and what "archived"/"cancelled" read
 * as. Every surface now resolves through `WORKFLOW_STATUS_META`, so the pill in the
 * list, the header, the sidebar, and the step tracker always speak the same language.
 *
 * Covers workflow-definition statuses (draft/published/archived), run statuses
 * (pending/executing/completed/failed/cancelled), and the step-run alias `running`
 * (same presentation as `executing`).
 *
 * On-brand semantics (no rainbow): neutral for idle, brand ink (Counsel Black) for "done",
 * dusty-rose accent for "active", destructive for failure.
 */
import type { Component } from 'vue'
import { CheckCircle, XCircle, Loader, Clock, FileText, Archive } from 'lucide-vue-next'

export interface StatusMeta {
  /** i18n key for the human label. Resolved by the caller via `t()`. */
  labelKey: string
  /** Leading icon (shown in headers/tables and the step tracker). */
  icon: Component
  /** Pill classes for `WorkflowStatusBadge` (background + text). */
  tone: string
  /** Text color for a standalone icon (run-detail step tracker). */
  iconColor: string
  /** Whether the icon should spin (active states). */
  spin?: boolean
}

const IDLE_TONE = 'bg-muted text-muted-foreground'
const DONE_TONE = 'bg-primary-light text-primary'
const ACTIVE_TONE = 'bg-primary-lighter text-primary'
const FAIL_TONE = 'bg-destructive/10 text-destructive'
const FADED_TONE = 'bg-muted text-muted-foreground/70'

const ACTIVE: StatusMeta = {
  labelKey: 'runs.status.executing', icon: Loader, tone: ACTIVE_TONE, iconColor: 'text-primary', spin: true,
}

export const WORKFLOW_STATUS_META: Record<string, StatusMeta> = {
  // workflow-definition statuses
  draft:     { labelKey: 'common.draft',       icon: FileText,    tone: IDLE_TONE,  iconColor: 'text-muted-foreground' },
  published: { labelKey: 'common.published',   icon: CheckCircle, tone: DONE_TONE,  iconColor: 'text-primary' },
  archived:  { labelKey: 'common.archived',    icon: Archive,     tone: FADED_TONE, iconColor: 'text-muted-foreground/70' },
  // run statuses
  pending:   { labelKey: 'runs.status.pending',   icon: Clock,       tone: IDLE_TONE, iconColor: 'text-muted-foreground/40' },
  executing: ACTIVE,
  running:   ACTIVE, // step-run alias
  completed: { labelKey: 'runs.status.completed', icon: CheckCircle, tone: DONE_TONE, iconColor: 'text-primary' },
  failed:    { labelKey: 'runs.status.failed',    icon: XCircle,     tone: FAIL_TONE, iconColor: 'text-destructive' },
  cancelled: { labelKey: 'runs.status.cancelled', icon: XCircle,     tone: FADED_TONE, iconColor: 'text-muted-foreground/70' },
}

const FALLBACK: StatusMeta = {
  labelKey: '', icon: Clock, tone: IDLE_TONE, iconColor: 'text-muted-foreground',
}

/** Presentation metadata for a status, with a neutral fallback for unknown values. */
export function statusMeta(status: string | null | undefined): StatusMeta {
  return (status && WORKFLOW_STATUS_META[status]) || FALLBACK
}
